import React from 'react';
import { Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { EventInput } from '@fullcalendar/core';
import { CalendarEventStatusEnum } from 'src/enums/ECalendarEvents';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { openModal } from '@/store/modal/slice';
import { MODAL_IDS } from 'src/enums/EModal';

interface CalendarAppointmentsListProps {
	events: EventInput[];
	date: string;
}

function formatKey(key: string) {
	return key
		.split('_')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(' ');
}

export const CalendarAppointmentsList: React.FC<CalendarAppointmentsListProps> = ({
	events,
	date,
}) => {
	const dispatch = useAppDispatch();
	const visibleEvents = events.slice(0, 2);
	const hasMore = events.length > 2;

	const statusColorMap: Record<string, string> = {
		[CalendarEventStatusEnum.CONFIRMED]: 'green',
		[CalendarEventStatusEnum.SCHEDULED]: 'green',
		[CalendarEventStatusEnum.RESCHEDULED]: 'gold',
		[CalendarEventStatusEnum.CANCELLED]: 'red',
	};

	const renderEvent = (event: EventInput, idx: number) => {
		const extraInfo = event.extendedProps?.extra_display_event_info;
		const extendedProps = event.extendedProps;
		return (
			<div key={idx} className="bg-gray-50 rounded-lg p-4 mb-3 border-l-4 border-violet-400">
				<div className="flex items-center gap-2 mb-1">
					<span className="text-gray-700 font-semibold text-base">
						{dayjs(event.start.toString()).format('h:mm A')}
					</span>
					<Tag
						color={statusColorMap[event.status as string] || 'blue'}
						style={{ textTransform: 'capitalize', fontWeight: 600 }}
					>
						{event.status.toLowerCase()}
					</Tag>
				</div>
				<div className="font-semibold text-gray-900 mb-1">{event.title}</div>
				{extendedProps?.attendee?.name && (
					<div className="text-gray-700 text-sm">{extendedProps?.attendee.name}</div>
				)}
				{extendedProps?.attendee?.phone_number && (
					<div className="text-gray-500 text-xs">{extendedProps?.attendee.phone_number}</div>
				)}
				<div className="text-gray-400 text-xs mt-1">
					<b>Duration:</b> {extendedProps?.duration_minutes} minutes
				</div>
				{extraInfo && typeof extraInfo === 'object' && (
					<div className="mt-1">
						{Object.entries(extraInfo).map(([key, value]) => {
							const strValue = String(value);
							const truncated = strValue.length > 40 ? strValue.slice(0, 37) + '...' : strValue;
							return (
								<div key={key} className="text-gray-500 text-xs truncate">
									<span className="font-medium text-gray-600">{formatKey(key)}:</span>{' '}
									<Tooltip title={strValue} placement="topLeft">
										<span>{truncated}</span>
									</Tooltip>
								</div>
							);
						})}
					</div>
				)}
			</div>
		);
	};

	return (
		<div className="bg-white rounded-xl shadow p-4 mb-4">
			<div className="font-semibold text-gray-700 mb-3">
				Appointments for {dayjs(date).format('dddd, MMM D')}
			</div>
			{visibleEvents.length === 0 && <div className="text-gray-400 text-sm">No appointments</div>}
			{visibleEvents.map(renderEvent)}
			{hasMore && (
				<button
					className="mt-2 text-violet-600 font-semibold hover:underline"
					onClick={() =>
						dispatch(
							openModal({ modalId: MODAL_IDS.CALENDAR_APPOINTMENTS_MODAL, data: { events, date } }),
						)
					}
				>
					Show more
				</button>
			)}
		</div>
	);
};
